import { DatePipe, NgClass } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, OnInit, computed, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { FeatureGroupSearchComponent } from 'listings/components/feature-group-search/feature-group-search.component';
import { filter, map, startWith } from 'rxjs';
import { IMAGES } from 'shared/constants/imagePaths.constants';
import { Language, supportedLanguages } from 'shared/constants/languages';
import { Breadcrumb } from 'shared/interfaces/breadcrumb.interface';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { ButtonActionConfig, UserNotificationActionsService } from 'users/services/user-notification-actions.service';
import { NotificationStatusEnum, NotificationStatusList } from '../../constants/notification-statuses';
import { EventBusService, HeaderService, UserApiService } from '../../services';
import { FileService } from '../../services/file.service';
import { NotificationsService } from '../../services/notifications.service';
import { MenuComponent } from '../menu/menu.component';

const featureGroupSearchUrlDisallowList: string[] = [
    'photos',
    'customContent',
    'categories',
    'photoOrganizer',
    'roomTypeManager',
    'listingOverview',
    'roomOverview',
    'history',
    'outlet',
    'distribution',
    'distributionTracking',
    'policy',
    'ratePlan',
    'location',
    'roomType',
    'roomInformation',
    'accessibility',
    'bathroom',
    'children',
    'climateControl',
    'clothing',
    'entertainment',
    'features',
    'kitchen',
    'refreshments',
    'roomLayout',
    'safety',
    'new',
];

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [RouterLink, NgClass, TranslateModule, DatePipe, MenuComponent, FeatureGroupSearchComponent],
})
export class HeaderComponent implements OnInit, AfterViewInit {
    private readonly languageAppMenu = viewChild<MenuComponent>('languageAppMenu');
    private readonly userMenu = viewChild<MenuComponent>('userMenu');
    private readonly notificationsMenu = viewChild<MenuComponent>('notificationsMenu');

    readonly isVisible = input<boolean>(false);

    readonly isShiji = signal<boolean>(false);
    readonly currentLanguage = signal<string>('');
    readonly notificationsToShow = signal<any[]>([]);
    readonly newNotificationCount = signal<number>(0);

    readonly latestNotifications = computed(() => {
        return this.notificationsToShow().map((notification) => {
            const buttonConfig = this.userNotificationActionsService.getActionButtonsForNotification(notification);
            return { ...notification, buttonConfig: buttonConfig.length > 0 ? buttonConfig[0] : undefined };
        });
    });

    public breadcrumbs: Breadcrumb[] = [];
    public supportedLanguages = supportedLanguages;
    public userMenuVisible: boolean = false;
    public notificationsMenuVisible: boolean = false;
    protected IMAGES = IMAGES;
    featureFlagEnum = FeatureFlagEnum;

    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    readonly isListingDetailsPage = computed(() => {
        const url = this.currentUrl();
        return (url.includes('/listings/details/') || url.includes('/listings/bulk/')) && !/\/outlet\/[^/]+/.test(url);
    });

    readonly isOutletDetailsPage = computed(() => /\/outlet\/[^/]+/.test(this.currentUrl()));

    readonly isOnListingsOrOutletDetailsPage = computed(() => this.isListingDetailsPage() || this.isOutletDetailsPage());

    readonly showFeatureGroupSearchBasedOnUrl = computed<boolean>(() => {
        const url = this.currentUrl();
        return !featureGroupSearchUrlDisallowList.some((disallowedUrlFragment) => url.includes(disallowedUrlFragment));
    });

    constructor(
        private eventBus: EventBusService,
        private translate: TranslateService,
        private headerService: HeaderService,
        private authenticationService: AuthenticationService,
        public userApiService: UserApiService,
        public notificationsService: NotificationsService,
        public destroyRef: DestroyRef,
        private router: Router,
        private fileService: FileService,
        private eventBusService: EventBusService,
        private userNotificationActionsService: UserNotificationActionsService,
        public featureFlagService: FeatureFlagService,
    ) {}

    public ngOnInit(): void {
        this.currentLanguage.set(this.translate.getCurrentLang() ?? supportedLanguages[0].languageCode);

        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((notification: any) => {
                if (!notification || NotificationStatusList[notification.Status] == NotificationStatusEnum.IN_PROGRESS) {
                    return;
                }
                this.increaseNotificationsCount();
            });

        this.userApiService
            .getLoginInformationContinuously()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loginInfo) => {
                this.isShiji.set(loginInfo.isShiji);
                this.getNotificationsData();
            });
    }

    public ngAfterViewInit() {
        this.headerService.registerHeader(this);
    }

    public changeLanguage(language: Language) {
        this.eventBus.dispatch('changeLanguage', language.languageCode);
        this.currentLanguage.set(language.languageCode);
        this.languageAppMenu()?.closeMenu();
    }

    public logout() {
        this.userMenu()?.closeMenu();
        this.authenticationService.logout(this.router.url || '/');
    }

    public navigateToMyUserProfile() {
        this.userMenu()?.closeMenu();
        this.router.navigate(['/my-profile']);
    }

    public navigateToNotifications() {
        this.notificationsMenu()?.closeMenu();
        this.router.navigate(['/notifications']);
    }

    public getUnreadNotifications() {
        this.notificationsToShow.set([]);
        this.notificationsService
            .getUnreadNotifications(1, 2)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((unreadNotifications: any) => {
                this.notificationsToShow.set(unreadNotifications.data);
                if (!this.notificationsToShow() || this.notificationsToShow().length == 0) {
                    this.navigateToNotifications();
                } else {
                    this.markNotificationsAsRead();
                }
            });
    }

    public downloadFile(downloadLink: string) {
        this.fileService.downloadExcelFile(downloadLink);
    }

    public handleGenericButtonAction(buttonConfig: ButtonActionConfig) {
        buttonConfig.clickAction();
        this.notificationsMenu()?.closeMenu();
    }

    private increaseNotificationsCount() {
        this.newNotificationCount.update((current) => current + 1);
    }

    private getNotificationsData() {
        if (!this.isVisible) {
            return;
        }

        this.notificationsService
            .getNewNotificationsCount()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((newNotifications: any) => {
                this.newNotificationCount.set(newNotifications);
            });
    }

    private markNotificationsAsRead() {
        this.notificationsService
            .markNotificationsAsRead()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.newNotificationCount.set(0);
            });
    }
}
